// src/components/SignupPopup.jsx
// Shows once per browser session, 9 seconds after page load, to users who aren't logged in.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Custom SVG graphic — no external image dependency

const SESSION_KEY = "circuit_signup_popup_shown";
const SHOW_DELAY_MS = 9000; // 8–10 seconds

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function SignupPopup() {
  const [visible, setVisible] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't show to logged-in users
    if (user) return;

    // Already shown this session — don't show again
    if (sessionStorage.getItem(SESSION_KEY) === "true") return;

    const timer = setTimeout(() => {
      setVisible(true);
      sessionStorage.setItem(SESSION_KEY, "true");
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user]);

  function handleClose() {
    setVisible(false);
  }

  function handleSignup() {
    setVisible(false);
    navigate("/login");
  }

  if (!visible) return null;

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "signupPopupFadeIn 0.25s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, overflow: "hidden",
          width: "100%", maxWidth: 440, boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          position: "relative", animation: "signupPopupSlideUp 0.3s ease",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 12, right: 12, width: 32, height: 32,
            borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none",
            color: "#fff", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", zIndex: 1,
          }}
        >
          <CloseIcon />
        </button>

        <div style={{ width: "100%", height: 200, background: "linear-gradient(135deg, #0d1326 0%, #1a2744 100%)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
          <svg width="100%" height="100%" viewBox="0 0 440 200" style={{ position: "absolute", inset: 0 }}>
            <circle cx="60" cy="40" r="70" fill="rgba(0,170,221,0.12)" />
            <circle cx="400" cy="170" r="90" fill="rgba(0,170,221,0.08)" />
            <circle cx="370" cy="30" r="40" fill="rgba(233,30,140,0.10)" />
          </svg>
          <svg width="76" height="76" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ position: "relative", zIndex: 1 }}>
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          <div style={{ position: "absolute", bottom: 16, left: 0, right: 0, textAlign: "center", zIndex: 1 }}>
            <span style={{ color: "var(--cyan, #00aadd)", fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase" }}>
              Member Perks Await
            </span>
          </div>
        </div>

        <div style={{ padding: "24px 24px 28px", textAlign: "center" }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: "#1a1a2e" }}>
            Join Circuit Printing
          </h3>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 20, lineHeight: 1.6 }}>
            Sign up to track your orders, save your details for faster checkout, and get exclusive offers.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={handleClose}
              style={{
                flex: 1, padding: "12px 16px", borderRadius: 8,
                border: "1.5px solid #e5e7eb", background: "#fff",
                color: "#666", fontWeight: 600, fontSize: 14, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSignup}
              style={{
                flex: 1, padding: "12px 16px", borderRadius: 8,
                border: "none", background: "var(--cyan, #00aadd)",
                color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes signupPopupFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes signupPopupSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
